import { Icon } from 'app/components/common/Icon';
import { ITableColumn, TableRow } from 'app/components/table/table.model';
import { AgenturModel } from 'app/models/agentur.model';
import { SteartModel } from '../core/steart.model';
import { AgtType } from '../agentur.model';
import { observer } from 'mobx-react';
import Config from 'Config';

const currentPlanYear = Config.currentPlanYear;

export const getAgtListCols = (steart: SteartModel): ITableColumn<AgenturModel, number>[] => {
	let ret = VblAgenturListCols;
	if (steart.isLeiter || steart.isDVSAdmin) {
		ret = LeiterAgenturListCols;
	}
	if (steart.isMulti) {
		ret = MultiAgenturListCols;
	}
	if (steart.isSpezi) {
		ret = SpeziAgenturListCols;
	}
	if (steart.name === 'EDP') {
		// remove EDP column for EPDs
		ret = MultiAgenturListCols.filter((c) => c.label !== 'EDP');
	}
	if (steart.id === 123) {
		ret = AdminAgenturListCols;
	}

	return ret;
};

export interface IGpText {
	vm: AgenturModel;
}

export const GpShort = observer((props: IGpText) => {
	const vm = props.vm;
	return (
		<>
			{vm.agtType === AgtType.agt && vm.gpShort}
			{vm.agtType === AgtType.ada && <span className="tag is-small is-green is-marginless">ADA</span>}
		</>
	);
});

export const baseAgtCols: ITableColumn<AgenturModel, number>[] = [
	{
		label: 'Agentur',
		path: 'name',
		render: (row: TableRow<AgenturModel>) => {
			return (
				<>
					{row.data.shouldBeTemp && (
						<span>
							<Icon iconClass={'drafting-compass'} faModifier="fal" />{' '}
						</span>
					)}
					<span>{row.data.name}</span>
					{!row.data.visTeilnahme && Config.showVisTeilnahme && (
						<span data-tooltip="Keine VIS-Teilnahme" data-tooltip-size="small">
							{' '}
							<Icon iconClass={'exclamation-circle'} faModifier="far color-red" />
						</span>
					)}
				</>
			);
		},
	},
	{
		label: 'GP',
		path: 'gpShort',
		render: (row: TableRow<AgenturModel>) => {
			return <GpShort vm={row.data} />;
		},
	},
	{
		label: 'VTNR',
		path: 'agtId',
	},
];

export const FocusThemnenCol = (row: TableRow<AgenturModel>) => {
	return (
		<>
			<div className="tag-list whitespace-wrap">
				{row.data.focusThemen.map((x) => (
					<span className="tag is-small" style={{ backgroundColor: x.def.background, color: x.def.color || 'inherit' }} key={x.def.keyThema}>
						{x.def.themaShort}
					</span>
				))}
			</div>
		</>
	);
};

export const AdminAgenturListCols: ITableColumn<AgenturModel, number>[] = [
	...baseAgtCols,
	{
		label: 'VD ',
		path: 'vdId',
	},
	{
		label: 'Gs ',
		path: 'gsId',
	},
	{
		//label: 'Fokusthemen ' + year,
		label: 'Fokusthemen' + Config.currentPlanYear,
		path: 'data.focusThemen.length',
		render: FocusThemnenCol,
		sortBy: 'focusThemenSortString',
	},
	{
		label: 'VKAT ',
		path: 'vertKat.name',
	},
	{
		label: 'Teilhaber ',
		path: 'thCount',
	},
];



export const VblAgenturListCols: ITableColumn<AgenturModel, number>[] = [
	...baseAgtCols,
	{
		//label: 'Fokusthemen ' + year,
		label: 'Fokusthemen' + Config.currentPlanYear,
		path: 'data.focusThemen.length',
		render: FocusThemnenCol,
		sortBy: 'focusThemenSortString',
	},

	{
		label: 'BP ' + currentPlanYear,
		path: 'bp.myCountWithValues',
		render: (row: TableRow<AgenturModel>) => {
			if (!row.data.bp) {
				return <></>;
			}
			const bp = row.data.bp;
			return (
				<>
					{bp.myCountWithValues} / {bp.myPoses.length}
				</>
			);
		},
	},
	{
		label: `BP Spezi ` + currentPlanYear,
		path: 'bp.otherCountWithValues',
		render: (row: TableRow<AgenturModel>) => {
			if (!row.data.bp) {
				return <></>;
			}
			const bp = row.data.bp;
			return (
				<>
					{bp.otherCountWithValues} / {bp.otherPoses.length}
				</>
			);
		},
	},
	{
		label: 'Schwerpunkt ' + currentPlanYear,
		path: 'vdId',
		sortBy: 'schwerpunkt.firstSelected',
		render: (row: TableRow<AgenturModel>) => {
			if (!row.data.schwerpunkt) {
				return <></>;
			}
			const sp = row.data.schwerpunkt;

			return (
				<div className="agt-list-schwerpunkt">
					{sp.selectedRolesAsList.map((r) => (
						<span className={`tag color-${r}`} key={r}>
							{r}
						</span>
					))}
				</div>
			);
		},
	},
];

export const SpeziAgenturListCols = VblAgenturListCols.map((c) => {
	// change label for Spezis
	if (c.path === 'bp.otherCountWithValues') {
		c.label = `BP VBL ` + currentPlanYear;
	}
	return c;
});

export const LeiterAgenturListCols: ITableColumn<AgenturModel, number>[] = [
	...baseAgtCols,
	{
		label: 'Schwerpunkt ' + currentPlanYear,
		path: 'vdId',
		sortBy: 'schwerpunkt.firstSelected',
		render: (row: TableRow<AgenturModel>) => {
			if (!row.data.schwerpunkt) {
				return <></>;
			}
			const sp = row.data.schwerpunkt;

			return (
				<div className="agt-list-schwerpunkt">
					{sp.selectedRolesAsList.map((r) => (
						<span className={`tag color-${r}`} key={r}>
							{r}
						</span>
					))}
				</div>
			);
		},
	},
	{
		//label: 'Fokusthemen ' + year,
		label: 'Fokusthemen' + Config.currentPlanYear,
		path: 'data.focusThemen.length',
		render: FocusThemnenCol,
	},
];
export const MultiAgenturListCols: ITableColumn<AgenturModel, number>[] = [
	...baseAgtCols,
	{
		label: 'Bürotyp',
		path: 'buerotyp',
	},
	{
		//label: 'Fokusthemen ' + year,
		label: 'Fokusthemen' + Config.currentPlanYear,
		path: 'data.focusThemen.length',
		render: FocusThemnenCol,
	},
	{
		label: 'EDP',
		path: 'users',
		render: (row: TableRow<AgenturModel>) => {
			const edp = row.data.users?.filter((u) => u.steart.name === 'EDP');
			return (
				<>
					{edp.map((r, i) => (
						<span key={r.bensl + i}>{r.name}</span>
					))}
				</>
			);
		},
	},

	{
		label: 'Schwerpunkt ' + currentPlanYear,
		path: 'vdId',
		sortBy: 'schwerpunkt.firstSelected',
		render: (row: TableRow<AgenturModel>) => {
			if (!row.data.schwerpunkt) {
				return <></>;
			}
			const sp = row.data.schwerpunkt;

			return (
				<div className="agt-list-schwerpunkt">
					{sp.selectedRolesAsList.map((r) => (
						<span className={`tag color-${r}`} key={r}>
							{r}
						</span>
					))}
				</div>
			);
		},
	},
	{
		label: 'Mietmodell',
		path: 'mietmodell',
	},
	{
		label: 'Steart',
		path: 'steartNameShortOrLongOrId',
	},
	{
		label: 'Geräte',
		path: 'anzgeraete',
	},
	{
		label: 'VTNR Typ',
		path: 'vtnrType',
	},
];
